import { Component, HostListener, inject, linkedSignal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { L10nTranslationModule } from 'angular-l10n';
import { SessionService } from '@wonder-core-ng/session-store/session.service';
import { AiChatComponent } from '../ai-chat/ai-chat.component';
import { Permission } from '../interfaces/permission.enum';
import { GlobalAssistantService } from './global-assistant.service';

/**
 * Launcher and panel of the app's single assistant chat (WB-155). It lives in the header bar, which
 * every BI app already renders, so no host shell needs to change. The panel floats over the routed
 * view; the builder screens no longer embed a chat of their own.
 *
 * While the panel is closed the launcher tells the user a reply arrived: a marker until they open
 * it, and for a few seconds a bubble with the start of the reply.
 */
@Component({
  selector: 'wbi-global-assistant',
  imports: [MatButtonModule, MatIconModule, MatTooltipModule, L10nTranslationModule, AiChatComponent],
  templateUrl: './global-assistant.component.html',
  styleUrl: './global-assistant.component.scss',
})
export class GlobalAssistantComponent {
  protected readonly assistant = inject(GlobalAssistantService);
  private readonly session = inject(SessionService);

  /** For every user of the app: whoever cannot build is answered by the guide agent (WB-154). */
  protected readonly available = this.session.hasPermissions([Permission.BasicAccess]);

  /**
   * The chat is created the first time the panel opens and kept afterwards, only hidden: closing
   * the panel must not throw away the draft, the scroll position or the history drawer.
   */
  protected readonly mounted = linkedSignal<boolean, boolean>({
    source: () => this.assistant.isOpen(),
    computation: (open, previous) => open || (previous?.value ?? false),
  });

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (this.assistant.isOpen()) this.assistant.close();
  }

  protected dismissBubble(event: Event): void {
    event.stopPropagation();
    this.assistant.hideBubble();
  }
}
